# WHAT THE BROWSER CAN TELL US ABOUT A LOAD THAT FAILED — card
# 6ad395b84889e2c887c26c07 (group:load-failure-diagnostics).
#
# PRODUCTION, 2026-10-17, fingerprint 5467a4a6. Two rows six seconds apart,
# Safari 15.6.1 on macOS 10.15.7, client 3.47 (the client just shipped), on
# /app#/account:
#
#   ChunkLoadError: Loading chunk 1882 failed.
#   (error: https://assets.elephantlearning.com/clientDist/1882e748f138ea33.js)
#
# The retries (lazy_route), the once-per-chunk reload and the crash screen's
# refresh -> dashboard escalation all ran. The file was fine from where we
# checked it (200, 21305 bytes, CORS *). And the rows could not say WHY the
# browser gave up, because all they carried was the URL, the count and the
# step taken. Owner: "I thought we were adding logging to see why this
# happened." This module is that logging, and it goes on the SAME CrashLog row
# (error_boundary.reportCrash puts it in `context.loadDiagnostics`), not into a
# logger of its own.
#
# WHAT A BROWSER WILL AND WILL NOT SAY. The honest part, and why every field
# below can come back 'unavailable':
#
#   - A <script> tag that fails (webpack's chunks) fires `error` and NOTHING
#     ELSE. A 404, a dropped connection, a CSP block and a content blocker all
#     look identical from the page. webpack's own `type` is the one fact
#     there is: 'error' (the script element failed), 'missing' (it LOADED but
#     never registered its chunk — a parse error, or the wrong file), 'timeout'.
#   - A module import() that fails (hosted screens) rejects with an engine
#     sentence and no status: WebKit "Importing a module script failed.",
#     Chrome "Failed to fetch dynamically imported module", Firefox "error
#     loading dynamically imported module". Network, HTTP status and parse
#     error all read the same.
#   - Resource Timing MAY have an entry for the request. Safari often makes
#     none for a failed load ('noEntry'). `responseStatus` is Chrome-only
#     ('unavailable' elsewhere). A cross-origin file served without
#     Timing-Allow-Origin — our CDN — gives sizes 0 and the request/response
#     phases withheld ('detail: withheld').
#   - A CSP block fires `securitypolicyviolation`, which we listen for. A
#     content blocker fires nothing.
#
# So this file never names a cause. It records what the browser said, says
# plainly what it did not, and leaves the reading to whoever reads the row.
#
# THE PROBE IS A SECOND REQUEST, AND IT IS LABELLED AS ONE. When a load has
# failed for good, the boundary asks for the same URL once more with fetch(),
# which DOES see a status. That answer is about a LATER request on this device
# — not the failed one — and the row says so (`probe.later: true`, and how long
# after). Bounded (PROBE_TIMEOUT_MS), never throws, never reads the body, sends
# cookies only to the page's own origin (`credentials: 'same-origin'`), no
# referrer, and only for a URL our own loader built (webpack's `request`,
# componentCookieClient's `moduleUrl`) — never one parsed out of a message.
#
# THE FIRST PAGE LIFE IS KEPT. lazy_route's last resort is one reload, and that
# reload files nothing — so on 5467a4a6 the first failure, the one before the
# reload, left no trace anywhere. Each failed load is kept in sessionStorage
# (this tab only, MAX_HISTORY of them, expires after HISTORY_MS) and the next
# row carries it as `prior`, with the page id it came from. Cleared when the
# app has stayed up (error_boundary's healthy timer).
#
# BOUNDED. CrashLog.report keeps `context` as JSON capped at 3000 characters
# and slices whatever is longer, which would cut the JSON in half. fit() keeps
# this block under BUDGET by dropping detail in a fixed order, so what reaches
# the row is always whole.

HISTORY_KEY = 'elfeetLoadDiagnostics'
HISTORY_MS = 10 * 60 * 1000
MAX_HISTORY = 2
export BUDGET = 1400
export PROBE_TIMEOUT_MS = 2500
CSP_KEEP = 8

# One id per page life, so a row's `page` and a later row's `prior[].page` can
# be matched up.
pageId = Math.random().toString(36).slice(2, 8)
violations = []
cspWatching = null
# LOADS STILL IN FLIGHT — card 6ad3bcf44889e2c887c26c2d. A lazy route whose
# module has not settled shows its Suspense fallback, and the boundary around
# it is mounted all the same; it is not a destination that came up healthy.
inFlight = 0

export getPageId = -> pageId

# How many loads opened by beginLoad have neither returned nor thrown yet.
export loadsInFlight = -> inFlight

defaultWindow = -> if typeof window != 'undefined' then window else null

perfNow = (win) ->
  try
    n = win?.performance?.now?()
    if typeof n is 'number' then n else Date.now()
  catch e
    Date.now()

round = (n) -> if typeof n is 'number' and isFinite(n) then Math.round(n) else 'unavailable'

# Origin and path only. A query or fragment can carry a credential (a sign-in
# link's access_token rides in one), so neither is ever written down; the row
# says one was there. So can the userinfo of `https://user:password@host/` —
# card 6ad3bcf44889e2c887c26c2f — which is why the URL is PARSED and rebuilt
# from its origin (which never includes userinfo) and path, never cut by
# pattern. What is not an http(s) URL is not written down at all, beyond the
# few CSP keywords a refusal names instead of a URL ('inline', 'eval', …) and
# a same-origin path, which has no authority to carry a credential in.
CSP_KEYWORD = /^(?:inline|eval|wasm-eval|self|data|blob|trusted-types-policy|trusted-types-sink)$/

export redactUrl = (url) ->
  return null unless url?
  try
    s = String(url)
    omitted = if /[?#]/.test(s) then '?(query omitted)' else ''
    return s if CSP_KEYWORD.test(s)
    if /^\/(?!\/)/.test(s)
      return s.match(/^([^?#@]*)/)[1].slice(0, 300) + omitted
    u = new URL(s)
    return '(non-http URL omitted)' unless u.protocol in ['http:', 'https:']
    (u.origin + u.pathname).slice(0, 300) + omitted
  catch e
    '(not a URL)'

# A URL inside free text (an error's message) is written down the same way.
scrubText = (text) ->
  String(text).replace /\b[a-z][a-z0-9+.-]*:\/\/[^\s)'"]+/gi, (u) -> redactUrl(u)

hasUserinfo = (url) ->
  try
    u = new URL(String(url))
    u.username isnt '' or u.password isnt ''
  catch e
    /^[a-z][a-z0-9+.-]*:\/\/[^\/?#]*@/i.test String(url)

absolute = (url, win) ->
  try
    new URL(String(url), win?.location?.href).href
  catch e
    String(url)

# The URL our own loader asked for. webpack puts it on the error as `request`;
# schema's componentCookieClient stamps `moduleUrl`. Only those two are TRUSTED
# (the probe goes to them); the message is a fallback for the record alone.
export loaderUrl = (error) ->
  return error.request if typeof error?.request is 'string' and error.request
  return error.moduleUrl if typeof error?.moduleUrl is 'string' and error.moduleUrl
  null

urlOf = (error) ->
  loaderUrl(error) ? (String(error?.message ? '').match(/\b(https?:\/\/[^\s)'"]+)/)?[1] ? null)

# What kind of failure the browser reported — its words, sorted, not a cause.
#   scriptElementError  webpack: the <script> fired `error` (network, HTTP
#                       status, CSP or a blocker — the page cannot tell which)
#   loadedNotRegistered webpack: the script LOADED and its chunk never
#                       registered (it did not parse, or it was not our file)
#   timeout             webpack's own chunk timeout
#   moduleImportFailed  an import() the engine refused, any reason
#   fetchFailed         a fetch() that never got an answer
#   parseError          a SyntaxError reached us directly
#   other               an error that is not a load failure's shape — the
#                       module loaded and THREW, say
export classify = (error) ->
  try
    msg = String(error?.message ? error ? '')
    if error?.name is 'ChunkLoadError'
      return switch error.type
        when 'error' then 'scriptElementError'
        when 'missing' then 'loadedNotRegistered'
        when 'timeout' then 'timeout'
        else 'chunkLoadError'
    return 'parseError' if error?.name is 'SyntaxError'
    return 'moduleImportFailed' if /Importing a module script failed|Failed to fetch dynamically imported module|error loading dynamically imported module/.test msg
    return 'fetchFailed' if /^(?:TypeError: )?(?:Load failed|Failed to fetch|NetworkError when attempting to fetch resource\.?)$/.test msg
    'other'
  catch e
    'other'

# Listen for CSP refusals from the first load on. A refusal that happened
# before this was installed is simply not seen, which is why it is installed
# as early as a load is attempted rather than when one fails.
export watchCsp = (win = defaultWindow()) ->
  return cspWatching if cspWatching?
  try
    doc = win?.document
    unless typeof doc?.addEventListener is 'function' and win?.SecurityPolicyViolationEvent?
      return cspWatching = false
    doc.addEventListener 'securitypolicyviolation', (ev) ->
      try
        violations.push
          uri: redactUrl(ev?.blockedURI)
          dir: String(ev?.effectiveDirective or ev?.violatedDirective or '').slice(0, 40)
          disp: String(ev?.disposition or '').slice(0, 10)
          t: perfNow(win)
        violations.shift() while violations.length > CSP_KEEP
      catch e
        null
    cspWatching = true
  catch e
    cspWatching = false

# The refusals that are about THIS url, inside this attempt.
cspFor = (url, since, win) ->
  return 'unsupported' unless cspWatching
  want = redactUrl(absolute(url, win))
  hits = for v in violations when v.t >= since - 5 and v.uri? and (v.uri is want or (want? and want.indexOf(v.uri) is 0))
    {uri: v.uri, dir: v.dir, disp: v.disp}
  if hits.length then hits.slice(-2) else 'none'

# The browser's own record of the request, if it kept one.
timingFor = (url, since, win) ->
  try
    perf = win?.performance
    return 'unsupported' unless typeof perf?.getEntriesByName is 'function'
    entries = (e for e in (perf.getEntriesByName(absolute(url, win)) or []) when e.startTime >= since - 5)
    e = entries[entries.length - 1]
    return 'noEntry' unless e?
    startMs: round(e.startTime - since)
    durMs: round(e.duration)
    status: (if typeof e.responseStatus is 'number' then e.responseStatus else 'unavailable')
    proto: (e.nextHopProtocol or 'unavailable')
    transfer: round(e.transferSize)
    body: round(e.encodedBodySize)
    # Cross-origin without Timing-Allow-Origin, the browser zeroes these.
    detail: (if (e.requestStart > 0 or e.responseStart > 0) then 'full' else 'withheld')
  catch err
    'unavailable'

environment = (win) ->
  out = {}
  try out.vis = String(win?.document?.visibilityState ? 'unavailable')
  try out.online = (win?.navigator?.onLine != false)
  out

pageFacts = (win) ->
  out = {}
  try out.nav = win?.performance?.getEntriesByType?('navigation')?[0]?.type ? 'unavailable'
  catch e then out.nav = 'unavailable'
  try out.conn = win?.navigator?.connection?.effectiveType ? 'unavailable'
  catch e then out.conn = 'unavailable'
  out

# ONE attempt, as it failed. `since` is perfNow() at the attempt's start.
export describeAttempt = (error, since, win = defaultWindow(), n = 1) ->
  try
    url = urlOf(error)
    now = perfNow(win)
    a =
      n: n
      cls: classify(error)
      ms: round(now - since)
      atS: Math.round(since / 100) / 10
    a.type = String(error.type).slice(0, 20) if error?.name is 'ChunkLoadError' and error?.type?
    a.err = scrubText("#{error?.name or 'Error'}: #{String(error?.message ? error).split('\n')[0]}").slice(0, 120) if a.cls in ['other', 'parseError']
    if url?
      a.rt = timingFor(url, since, win)
      a.csp = cspFor(url, since, win)
    Object.assign a, environment(win)
  catch e
    {n: n, cls: 'other', describeFailed: true}

# A load that may take several attempts. lazy_route.loadChunk opens one,
# reports each failure to it, and hands the result to the boundary on the
# error it finally throws.
export beginLoad = (name, win = defaultWindow()) ->
  watchCsp win
  attempts = []
  firstUrl = null
  started = null
  open = true
  inFlight += 1
  started: ->
    started = perfNow(win)
  failed: (error) ->
    try
      firstUrl ?= urlOf(error)
      attempts.push describeAttempt(error, started ? perfNow(win), win, attempts.length + 1)
    catch e
      null
  # The load returned or gave up. A load that ends in lazy_route's reload never
  # settles: the page is leaving, and nothing on it is healthy.
  settled: ->
    if open
      open = false
      inFlight = Math.max(0, inFlight - 1)
  summary: ->
    try
      out = {v: 1, name: String(name).slice(0, 60), page: pageId}
      out.url = redactUrl(firstUrl) if firstUrl?
      Object.assign out, pageFacts(win)
      out.attempts = attempts.slice()
      out
    catch e
      {v: 1, name: String(name), page: pageId, summaryFailed: true}

# A load failure that did not come through loadChunk — the Mobile Shell inside
# a hosted screen, a lazy import of the shell's own. One attempt, described as
# the boundary sees it; its start is unknown, so timing is matched over the
# page life.
export describeError = (error, win = defaultWindow()) ->
  try
    url = urlOf(error)
    out = {v: 1, name: 'unrouted', page: pageId}
    out.url = redactUrl(url) if url?
    Object.assign out, pageFacts(win)
    a = describeAttempt(error, 0, win, 1)
    delete a.ms
    delete a.atS
    out.attempts = [a]
    out
  catch e
    null

# ── the probe ───────────────────────────────────────────────────────────────

delay = (ms) -> new Promise (resolve) -> setTimeout resolve, ms

export probe = (url, deps = {}) ->
  win = deps.window ? defaultWindow()
  doFetch = deps.fetch ? win?.fetch?.bind?(win)
  timeoutMs = deps.timeoutMs ? PROBE_TIMEOUT_MS
  try
    return Promise.resolve({later: true, skipped: 'noLoaderUrl'}) unless url?
    abs = absolute(url, win)
    return Promise.resolve({later: true, skipped: 'notHttp'}) unless /^https?:\/\//i.test(abs)
    # A URL that carries a user and password is not sent anywhere again.
    return Promise.resolve({later: true, skipped: 'credentialsInUrl'}) if hasUserinfo(abs)
    return Promise.resolve({later: true, skipped: 'unsupported'}) unless typeof doFetch is 'function'
    t0 = perfNow(win)
    ctrl = try (if win?.AbortController? then new win.AbortController() else null) catch e then null
    asked = Promise.resolve().then(-> doFetch abs,
      method: 'GET'
      cache: 'no-store'
      credentials: 'same-origin'
      mode: 'cors'
      redirect: 'follow'
      referrerPolicy: 'no-referrer'
      signal: ctrl?.signal
    ).then((res) ->
      header = (h) -> try (res.headers?.get?(h) ? null) catch e then null
      out =
        later: true
        ms: round(perfNow(win) - t0)
        status: res.status
        ok: res.ok
        type: res.type
        ctype: (header('content-type') ? 'unavailable')
        clen: (header('content-length') ? 'unavailable')
        redirected: res.redirected == true
      # The answer is the headers. The body is never read or kept.
      try res.body?.cancel?() catch e then null
      out
    ).catch((e) ->
      {later: true, ms: round(perfNow(win) - t0), error: scrubText("#{e?.name or 'Error'}: #{e?.message or e}").slice(0, 120)}
    )
    timedOut = delay(timeoutMs).then ->
      try ctrl?.abort() catch e then null
      {later: true, ms: timeoutMs, error: 'timeout'}
    Promise.race([asked, timedOut]).catch(-> {later: true, error: 'probeFailed'})
  catch e
    Promise.resolve({later: true, error: 'probeFailed'})

# ── the history across page lives ───────────────────────────────────────────

storage = (win) ->
  try win?.sessionStorage ? null
  catch e then null

readHistory = (win, now = Date.now()) ->
  try
    raw = storage(win)?.getItem HISTORY_KEY
    return [] unless raw
    list = JSON.parse raw
    return [] unless Array.isArray list
    (h for h in list when h? and typeof h.at is 'number' and 0 <= (now - h.at) <= HISTORY_MS)
  catch e
    []

# Kept short: the attempts' classes and durations, not their timing detail.
# `then` is what this page life did next — 'reload' (lazy_route's last resort,
# which files no row) or 'report' (handed to the boundary).
export remember = (summary, next, win = defaultWindow(), now = Date.now()) ->
  try
    return false unless summary?
    entry =
      at: now
      page: summary.page
      name: summary.name
      url: summary.url
      nav: summary.nav
      then: next
      attempts: ({n: a.n, cls: a.cls, type: a.type, ms: a.ms, vis: a.vis, online: a.online} for a in (summary.attempts or []))
    list = readHistory(win, now).concat([entry]).slice(-MAX_HISTORY)
    storage(win)?.setItem HISTORY_KEY, JSON.stringify(list)
    true
  catch e
    false

export forgetHistory = (win = defaultWindow()) ->
  try
    storage(win)?.removeItem HISTORY_KEY
    true
  catch e
    false

# Failed loads from EARLIER page lives in this tab, oldest first.
export priorLoads = (win = defaultWindow(), now = Date.now()) ->
  (h for h in readHistory(win, now) when h.page isnt pageId)

# ── what goes on the row ────────────────────────────────────────────────────

size = (v) ->
  try JSON.stringify(v).length catch e then Infinity

# Detail goes in this order until it fits: the earlier page lives, then the
# timing and CSP of every attempt but the last, then the middle attempts. What
# is left at worst is the last attempt's class and the count.
export fit = (diag, budget = BUDGET) ->
  try
    return null unless diag?
    return diag if size(diag) <= budget
    d = Object.assign {}, diag
    if d.prior?
      d.prior = ({page: p.page, then: p.then, cls: (a.cls for a in (p.attempts or []))} for p in d.prior)
      return d if size(d) <= budget
      delete d.prior
      d.priorDropped = true
      return d if size(d) <= budget
    if Array.isArray(d.attempts)
      last = d.attempts.length - 1
      d.attempts = for a, i in d.attempts
        if i is last then a else {n: a.n, cls: a.cls, type: a.type, ms: a.ms}
      return d if size(d) <= budget
      if d.attempts.length > 2
        d.attempts = [d.attempts[0], d.attempts[last]]
        return d if size(d) <= budget
    if d.probe?
      d.probe = {later: true, status: d.probe.status, error: d.probe.error}
      return d if size(d) <= budget
    a = d.attempts?[d.attempts.length - 1]
    {v: d.v, name: d.name, page: d.page, cls: a?.cls, n: d.attempts?.length, trimmed: true}
  catch e
    {trimmed: true}

# Everything the boundary needs, in one call that cannot reject: the load's own
# record (or one made from the error), the earlier page lives, the probe — and
# all of it fitted to the row.
export complete = (error, deps = {}) ->
  win = deps.window ? defaultWindow()
  try
    diag = error?.loadDiagnostics ? describeError(error, win)
    return Promise.resolve(null) unless diag?
    diag = Object.assign {}, diag
    prior = priorLoads(win)
    diag.prior = prior if prior.length
    probe(loaderUrl(error), deps)
      .then (p) ->
        diag.probe = p
        fit diag, deps.budget ? BUDGET
      .catch -> fit(diag, deps.budget ? BUDGET)
  catch e
    Promise.resolve(null)

# For the pins: a page life of its own.
export resetForTests = ->
  pageId = Math.random().toString(36).slice(2, 8)
  violations = []
  cspWatching = null
  inFlight = 0
